import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import RecipeCard from '../components/RecipeCard';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
    ArrowLeft, 
    Loader2, 
    Crown,
    Lock,
    BookOpen,
    Play,
    ChefHat
} from 'lucide-react';

export const DossierDetailPage = () => {
    const { slug } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    
    const [dossier, setDossier] = useState(null);
    const [loading, setLoading] = useState(true);
    const [recipes, setRecipes] = useState([]);

    useEffect(() => {
        loadDossier();
    }, [slug]);

    const loadDossier = async () => {
        try {
            const response = await api.getDossier(slug);
            setDossier(response.data);
            
            // If we have featured recipes details, use them
            if (response.data.featured_recipes_details) {
                setRecipes(response.data.featured_recipes_details);
            }
        } catch (error) {
            console.error('Error loading dossier:', error);
            if (error.response?.status === 404) {
                navigate('/dossiers');
            }
        } finally {
            setLoading(false);
        }
    };

    // Parse content to replace [[recipe:ID|text]] with links
    const parseContent = (content) => {
        if (!content) return '';
        
        // Replace [[recipe:ID|text]] with actual links
        const parsed = content.replace(
            /\[\[recipe:([a-f0-9-]+)\|([^\]]+)\]\]/g,
            (match, recipeId, text) => {
                return `<a href="/recipes/${recipeId}" class="text-purple-600 hover:text-purple-800 underline font-medium">${text}</a>`;
            }
        );
        
        // Convert line breaks to paragraphs
        return parsed.split('\n\n').map(p => `<p class="mb-4">${p}</p>`).join('');
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    if (!dossier) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <BookOpen className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
                    <h2 className="text-xl font-semibold">Dossier non trouvé</h2>
                    <Link to="/dossiers">
                        <Button className="mt-4">Retour aux dossiers</Button>
                    </Link>
                </div>
            </div>
        );
    }

    // Locked content for non-premium users
    if (dossier.is_locked) {
        return (
            <div className="min-h-screen py-8 md:py-12 relative overflow-hidden">
                <div className="blob-purple top-20 -right-40 opacity-20" />
                
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <Button
                        variant="ghost"
                        onClick={() => navigate('/dossiers')}
                        className="mb-6"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Retour aux dossiers
                    </Button>

                    {/* Cover Image */}
                    <div className="relative h-64 md:h-80 rounded-3xl overflow-hidden mb-8">
                        {dossier.cover_image ? (
                            <img 
                                src={dossier.cover_image} 
                                alt={dossier.title}
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <div className="w-full h-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
                                <BookOpen className="w-24 h-24 text-purple-300" />
                            </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        <div className="absolute bottom-6 left-6 right-6">
                            <Badge className="bg-gradient-to-r from-purple-600 to-pink-500 text-white border-0 mb-3">
                                <Crown className="w-3 h-3 mr-1" />
                                Premium
                            </Badge>
                            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white">
                                {dossier.title}
                            </h1>
                        </div>
                    </div>

                    {/* Preview */}
                    <div className="bg-muted/50 rounded-2xl p-6 mb-8">
                        <p className="text-muted-foreground italic">
                            {dossier.preview}
                        </p>
                    </div>

                    {/* Premium CTA */}
                    <div className="bg-gradient-to-r from-purple-600 to-pink-500 rounded-3xl p-8 text-white text-center">
                        <Lock className="w-16 h-16 mx-auto mb-4 opacity-80" />
                        <h2 className="font-heading text-2xl font-bold mb-2">
                            Contenu réservé aux membres Premium
                        </h2>
                        <p className="text-white/80 mb-6 max-w-xl mx-auto">
                            Débloquez ce dossier et tout le contenu Premium avec votre premier mois gratuit!
                        </p>
                        <Link to="/subscription">
                            <Button className="bg-white text-purple-600 hover:bg-white/90 font-bold px-8 py-6 rounded-full text-lg">
                                <Crown className="w-5 h-5 mr-2" />
                                Commencer mon essai gratuit
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen py-8 md:py-12 relative overflow-hidden">
            <div className="blob-purple top-20 -right-40 opacity-20" />
            <div className="blob-pink bottom-40 -left-40 opacity-20" />
            
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <Button
                    variant="ghost"
                    onClick={() => navigate('/dossiers')}
                    className="mb-6"
                >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Retour aux dossiers
                </Button>

                {/* Cover Image */}
                <div className="relative h-64 md:h-96 rounded-3xl overflow-hidden mb-8">
                    {dossier.cover_image ? (
                        <img 
                            src={dossier.cover_image} 
                            alt={dossier.title}
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <div className="w-full h-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
                            <BookOpen className="w-24 h-24 text-purple-300" />
                        </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-6 left-6 right-6">
                        <Badge className="bg-gradient-to-r from-purple-600 to-pink-500 text-white border-0 mb-3">
                            <Crown className="w-3 h-3 mr-1" />
                            Premium
                        </Badge>
                        <h1 className="font-heading text-3xl md:text-5xl font-bold text-white">
                            {dossier.title}
                        </h1>
                        <p className="text-white/70 mt-2">
                            {new Date(dossier.created_at).toLocaleDateString('fr-CA', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                            })}
                        </p>
                    </div>
                </div>

                {/* Video if available */}
                {dossier.video_url && (
                    <div className="mb-8">
                        <div className="aspect-video rounded-2xl overflow-hidden bg-black">
                            <iframe
                                src={dossier.video_url.replace('watch?v=', 'embed/')}
                                title={dossier.title}
                                className="w-full h-full"
                                allowFullScreen
                            />
                        </div>
                    </div>
                )}

                {/* Content */}
                <article className="prose prose-lg max-w-none mb-12">
                    <div 
                        className="text-foreground leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: parseContent(dossier.content) }}
                    />
                </article>

                {/* Featured Recipes */}
                {recipes.length > 0 && (
                    <div className="mt-12">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-500 rounded-2xl flex items-center justify-center">
                                <ChefHat className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h2 className="font-heading text-2xl font-bold">
                                    Recettes du dossier
                                </h2>
                                <p className="text-muted-foreground">
                                    {recipes.length} recettes à découvrir
                                </p>
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {recipes.map((recipe) => (
                                <RecipeCard key={recipe.id} recipe={recipe} />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DossierDetailPage;
